const generateColorSelectorHTML = (colorNames, colorValues) => `
<style>
  .personalisation-color-selector {
    margin-bottom: 4rem;
  }

  .colors-container {
    display: flex;
    justify-content: center;
    gap: 1.5rem;
    flex-wrap: wrap;
  }

  .radio-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    position: relative;
    cursor: pointer;
    font-size: 1rem;
    user-select: none;

    width: 5rem;
    height: 5.25rem;
    border: 0.0625rem solid var(--transparent-navy-navy-12);
    border-radius: 0.5rem
  }

  .radio-container input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
  }

  .radio-container.is-active {
    border-color: var(--opaque-lark-navy);
  }

  .color-name {
    font-family: var(--font-secondary);
    font-size: 0.75rem;
    font-weight: 400;
    line-height: 1rem;
    letter-spacing: 0.135rem;
    text-transform: uppercase;
  }

  .color-value {
    display: block;
    border-radius: 2rem;
    width: 1.5rem;
    height: 1.5rem;
    margin: 0 auto 0.75rem;
  }

  .personalisation-color-selector__title {
    font-size: 1.25rem;
    font-weight: 485;
    line-height: 1.625rem;
    letter-spacing: 0.025rem;
    text-transform: uppercase;
  }

  .color-value[data-color="white"] {
    border: 0.0625rem solid var(--transparent-navy-navy-12);
    width: 1.4375rem;
    height: 1.4375rem;
  }

  @media screen and (max-width: 68.75rem) {
    .personalisation-color-selector {
      margin-bottom: 1.75rem;
    }

    .personalisation-color-selector__title {
      font-size: 1.0625rem;
      font-weight: 485;
      line-height: 1.375rem;
      letter-spacing: 0.0213rem;
    }
  }
</style>

<div class="personalisation-color-selector" part="navigation">
  <p class="personalisation-color-selector__title">{{title}}</p>
  <div class="colors-container">
    ${colorNames.map((color, index) => `
      <label class="radio-container">
        <div class="input-wrapper">
          <input type="radio" name="colors" value="${color}">
        </div>
        <span style="background-color:${colorValues[index]}" data-color="${color.toLowerCase()}" class="color-value"></span>
        <span class="color-name">${color}</span>
      </label>
    `).join('')}
  </div>
</div>
`;
export class PersonalisationColorSelector extends HTMLElement {
  private readonly root: HTMLDivElement | null;
  private readonly colorTitle: HTMLElement | null;
  private readonly defaultRadio: HTMLInputElement | null;
  private colorNames: string[] = this.getAttribute('colors').split(',');
  private colorValues: string[] = this.getAttribute('color-values').split(',');
  private componentKey: string = this.getAttribute('key') || "";
  private componentTitle: string = this.getAttribute('title') || "";
  private radioContainers: NodeListOf<HTMLLabelElement>;

  constructor() {
    super();
    const shadowRoot = this.attachShadow({ mode: 'open' });

    const template = document.createElement('template');
    template.innerHTML = generateColorSelectorHTML(this.colorNames, this.colorValues);
    
    shadowRoot.appendChild(template.content.cloneNode(true));

    this.root = shadowRoot.querySelector('.personalisation-color-selector');

    this.colorTitle = this.root.querySelector('.personalisation-color-selector__title');
    this.defaultRadio = this.root.querySelector('input[type="radio"]');
    this.radioContainers = this.root.querySelectorAll('.radio-container');
  }

  connectedCallback() {
    const title = this.componentTitle;
    this.colorTitle.textContent = title;

    this.root.addEventListener('change', this.handleColorChange.bind(this));

    if (this.defaultRadio) {
      this.defaultRadio.checked = true;

      const selectedColorName = this.getAttribute('data-color-value') ? this.getAttribute('data-color-value') : this.defaultRadio.value;
      const colorIndex = this.colorNames.indexOf(selectedColorName);
  
      const defaultRadioEvent = new CustomEvent('color_selected', {
        detail: {
          colorName: selectedColorName,
          colorValue: this.colorValues[colorIndex],
          type: this.componentKey,
          selectedElement: this.defaultRadio
        },
        bubbles: true,
        composed: true
      });
  
      this.dispatchEvent(defaultRadioEvent);
  
      const changeEvent = new Event('change');
      this.defaultRadio.dispatchEvent(changeEvent);
    }
    
  }

  private handleColorChange(event: Event) {
    const selectedColorName = (event.target as HTMLInputElement).value;
    const colorIndex = this.colorNames.indexOf(selectedColorName);

    const customEvent = new CustomEvent('color_selected', {
      detail: {
        colorName: selectedColorName,
        colorValue: this.colorValues[colorIndex],
        type: this.componentKey
      },
      bubbles: true,
      composed: true
    });
    
    this.dispatchEvent(customEvent);
  }

  public setActiveColor(color) {
    this.radioContainers.forEach(rad_container => {
      const input = rad_container.querySelector('input');
      const containerIsActive = input.value === color;
      rad_container.classList[containerIsActive ? 'add' : 'remove']('is-active')
    })
  }
}

if(!customElements.get('personalisation-color-selector')) {
  customElements.define('personalisation-color-selector', PersonalisationColorSelector);
}